import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../firebase/config';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { processMentions } from '../firebase/helpers';
import { toast } from 'react-hot-toast';
import { Send, Image as ImageIcon, X, ArrowLeft } from 'lucide-react';

const MOODS = [
    { name: 'Reflective', emoji: '💭', label: 'Reflective' },
    { name: 'Joyful', emoji: '☀️', label: 'Joyful' },
    { name: 'Chill', emoji: '☕', label: 'Chill' },
    { name: 'Vent', emoji: '💨', label: 'Vent' },
    { name: 'Curious', emoji: '❓', label: 'Curious' },
    { name: 'Tired', emoji: '🥱', label: 'Tired' },
];

const CreatePostPage = ({ navigate }) => {
    const { currentUser, userProfile } = useAuth();
    const [text, setText] = useState('');
    const [mood, setMood] = useState('Reflective');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error("Image size must be under 5MB.");
                return;
            }
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if ((!text.trim() && !imageFile) || !currentUser || !userProfile) return;
        setIsSubmitting(true);

        try {
            let imageUrl = null;
            if (imageFile) {
                try {
                    const storageRef = ref(storage, `post_images/${currentUser.uid}/${Date.now()}_${imageFile.name}`);
                    await uploadBytes(storageRef, imageFile);
                    imageUrl = await getDownloadURL(storageRef);
                } catch (storageErr) {
                    console.warn("Storage upload error (fallback without image):", storageErr);
                }
            }

            const postDocRef = await addDoc(collection(db, "posts"), {
                text: text.trim(),
                imageUrl,
                mood,
                authorId: currentUser.uid,
                userId: currentUser.uid,
                authorProfile: {
                    name: userProfile.displayName,
                    photoURL: userProfile.photoURL,
                    username: userProfile.username
                },
                createdAt: serverTimestamp(),
                commentCount: 0,
                reactions: { fire: [], mindblown: [], funny: [], wholesome: [] }
            });

            if (text.trim()) {
                try {
                    await processMentions(
                        text.trim(),
                        currentUser.uid,
                        userProfile.displayName,
                        postDocRef.id,
                        text.substring(0, 50),
                        'post'
                    );
                } catch (mentionErr) {
                    console.warn("Mention processing warning:", mentionErr);
                }
            }

            toast.success("Fleeting thought posted! (Lives for 24h) ⏳");
            navigate('home');
        } catch (error) {
            console.error("Error creating post:", error);
            toast.error("Failed to share post. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-3.5 animate-fade-in pb-12">
            <button
                onClick={() => navigate('home')}
                className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-sky-500 dark:hover:text-sky-400 transition-colors p-1"
            >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to feed</span>
            </button>

            <div className="bg-white dark:bg-[#111827]/80 backdrop-blur-md border border-slate-200/90 dark:border-slate-800/80 p-4 sm:p-5 rounded-2xl sm:rounded-3xl shadow-xs space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800/80 pb-3">
                    <h2 className="text-base font-bold text-slate-900 dark:text-white">
                        Create Fleeting Thought
                    </h2>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20 uppercase tracking-wider">
                        24H Lifespan
                    </span>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Text Area */}
                    <div className="relative">
                        <textarea
                            rows={4}
                            maxLength={500}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="What's on your mind? Mention friends with @username..."
                            className="w-full bg-slate-50/80 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-3.5 sm:p-4 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40 resize-none transition-all"
                        />
                        <div className="absolute right-3 bottom-2.5 text-[11px] font-mono text-slate-400 dark:text-slate-500">
                            {500 - text.length}
                        </div>
                    </div>

                    {/* Image Preview */}
                    {imagePreview && (
                        <div className="relative rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 max-h-60 w-full animate-fade-in">
                            <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                            <button
                                type="button"
                                onClick={handleRemoveImage}
                                className="absolute top-2 right-2 p-1.5 bg-black/70 hover:bg-black text-white rounded-full transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {/* Mood Selector */}
                    <div>
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                            Select Mood
                        </label>
                        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5">
                            {MOODS.map(m => (
                                <button
                                    key={m.name}
                                    type="button"
                                    onClick={() => setMood(m.name)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all flex-shrink-0 ${
                                        mood === m.name
                                            ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-xs'
                                            : 'bg-slate-100 dark:bg-slate-800/80 border border-slate-200/70 dark:border-slate-700/70 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                    }`}
                                >
                                    <span>{m.emoji}</span>
                                    <span>{m.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Actions Bar */}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800/80">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageSelect}
                            accept="image/*"
                            className="hidden"
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-sky-500 dark:hover:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors"
                        >
                            <ImageIcon className="w-4 h-4" />
                            <span>{imageFile ? 'Change Photo' : 'Add Photo'}</span>
                        </button>

                        <button
                            type="submit"
                            disabled={isSubmitting || (!text.trim() && !imageFile)}
                            className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-400 hover:to-indigo-500 text-white font-bold text-xs rounded-xl shadow-md shadow-sky-500/20 hover:scale-102 active:scale-98 transition-all flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <Send className="w-3.5 h-3.5" />
                            <span>{isSubmitting ? 'Posting...' : 'Share Thought'}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreatePostPage;
